// @ts-check
/** Pop-up sales totals for one India calendar day (or all days): what the stand has taken, by how it was paid. */
import { useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminPosOrderService from '../services/adminPosOrderService';
import adminSalesEventService from '../services/adminSalesEventService';

const METHOD_LABELS = { cash: 'Cash', upi_qr: 'UPI QR', razorpay: 'Razorpay' };
const inr = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;

/** @param {{day: string, onDayChange: (day: string) => void, eventId: string, onEventChange: (id: string) => void}} props */
const PosSummaryStrip = ({ day, onDayChange, eventId, onEventChange }) => {
  const toast = useToast();
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    adminSalesEventService
      .list({ includeArchived: true })
      .then(setEvents)
      .catch(() => toast.error('Failed to load events'));
  }, [toast]);

  useEffect(() => {
    let stale = false;
    adminPosOrderService
      .getSummary({ day: day || undefined, event_id: eventId || undefined })
      .then((data) => !stale && setSummary(data))
      .catch(() => !stale && toast.error('Failed to load pop-up totals'));
    return () => {
      stale = true;
    };
  }, [day, eventId, toast]);

  return (
    <div className="mb-4 bg-card border border-border rounded-lg p-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <select
          value={eventId}
          onChange={(e) => onEventChange(e.target.value)}
          className="px-2 py-1 border border-input rounded-lg"
          aria-label="Event"
        >
          <option value="">All events</option>
          {events.map((event) => (
            <option key={event.id} value={event.id}>
              {event.name}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={day}
          onChange={(e) => onDayChange(e.target.value)}
          className="px-2 py-1 border border-input rounded-lg"
          aria-label="Day"
        />
        <button onClick={() => onDayChange('')} className="text-muted-foreground underline">
          All days
        </button>
        {summary && (
          <>
            <span>
              Paid: <strong>{summary.paid.count}</strong> sales &middot; <strong>{inr(summary.paid.total)}</strong>
            </span>
            {summary.partial && summary.partial.count > 0 && (
              <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 rounded-md">
                Advances (partially paid): {summary.partial.count} &middot; {inr(summary.partial.total)}
              </span>
            )}
            {Object.entries(summary.by_method).map(([method, row]) => (
              <span key={method} className="px-2 py-0.5 bg-muted rounded-md">
                {METHOD_LABELS[method] || method}: {row.count} &middot; {inr(row.total)}
              </span>
            ))}
            {summary.awaiting.count > 0 && (
              <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 rounded-md">
                Awaiting payment: {summary.awaiting.count} &middot; {inr(summary.awaiting.total)}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PosSummaryStrip;
